﻿import styles from './ButtonColumn.module.scss';
import clsx from 'clsx';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { FC, FunctionComponent, SVGProps } from 'react';
import { Tooltip } from 'uikit/tooltip/tooltip.component';

export type ButtonColumnColorSchema = 'primary' | 'secondary';

export interface ButtonColumnOptions<TRow> {
  id: string;
  onClick: (cell: CellContext<TRow, unknown>) => void;
  icon: FunctionComponent<SVGProps<any>>;
  /** @default secondary */
  colorSchema?: ButtonColumnColorSchema;
  disabled?: (cell: CellContext<TRow, unknown>) => boolean;
  visible?: (cell: CellContext<TRow, unknown>) => boolean;
  className?: string | ((cell: CellContext<TRow, unknown>) => string | undefined);
  iconClassName?: string;
  tooltip?: string;
}

// TODO: Add Tooltip if button is disabled (no design yet)
export function buttonColumn<TRow>(options: ButtonColumnOptions<TRow>): ColumnDef<TRow> {
  return {
    id: options.id,
    cell: (cell) => {
      if (typeof options.visible === 'function' && !options.visible(cell)) return <></>;

      const className = typeof options.className === 'function' ? options.className(cell) : options.className;

      return (
        <ColumnButtonWithTooltip
          Icon={options.icon}
          buttonClassName={className}
          tooltip={options.tooltip}
          colorSchema={options.colorSchema ?? 'secondary'}
          iconClassName={options.iconClassName}
          onClick={() => options.onClick(cell)}
          disabled={options.disabled?.(cell)}
        />
      );
    },
    header: '',
    meta: { cellClassName: styles.buttonCell, dontUseHeaderWidth: true },
  };
}

export const ColumnButtonWithTooltip: FC<{
  Icon: FunctionComponent<SVGProps<any>>;
  colorSchema: ButtonColumnColorSchema;
  tooltip?: string;
  disabled?: boolean;
  buttonClassName?: string;
  iconClassName?: string;
  onClick: () => void;
}> = ({ Icon, buttonClassName, tooltip, colorSchema, iconClassName, onClick, disabled }) => {
  return (
    <Tooltip text={tooltip ?? ''} disabled={!tooltip} hostStyles={styles.tooltipHost}>
      <div className={styles.buttonContainer}>
        <button
          className={clsx(styles.button, buttonClassName, {
            [styles.primary]: colorSchema === 'primary',
            [styles.secondary]: colorSchema === 'secondary' || !colorSchema,
          })}
          onClick={onClick}
          disabled={disabled}
        >
          <Icon className={clsx(styles.icon, iconClassName)} />
        </button>
      </div>
    </Tooltip>
  );
};
